<template>
  <div class="panel">
    <div class="flex items-center justify-between mb-4">
      <p class="m-0"><span class="text-blue-light font-light">{{ $t('pv_dashboard_project_progress') }}: </span> {{ projectProgress }}<span>%</span></p>
      <b-tooltip v-if="hasViewer" :label="$t('open_viewer')" position="is-left">
        <button type="button" class="text-blue-light hover:text-blue-dark px-2 py-1"
          @click="$emit('open-viewer')">
          <b-icon icon="external-link-alt" pack="fas" size="is-small"></b-icon>
        </button>
      </b-tooltip>
    </div>

    <div class="flex space-x-4">

      <div class="w-2/3 pb-5 relative">
        <div class="flex items-center justify-between mb-2 text-xs">
          <span class="text-gray-600" v-if="imgPlant.updatedAt">
            {{ $t('last_update') }}: {{ formattedUpdatedAt }}
          </span>
          <span v-else class="text-gray-400">
            {{ $t('pv_dashboard_no_updates') }}
          </span>
          <button
            type="button"
            @click="$emit('force-refresh')"
            :disabled="isLoadingImage"
            class="ml-2 px-2 py-1 text-blue-light hover:text-blue-dark disabled:opacity-50">
            <b-icon icon="sync-alt" pack="fas" size="is-small"></b-icon>
            <span class="ml-1">{{ $t('update') }}</span>
          </button>
        </div>

        <b-loading :is-full-page="false" :active="isLoadingImage"></b-loading>
        <b-image v-if="imgPlant.src"
            :src="imgPlant.src"
            :alt="imgPlant.name"
            @load="$emit('image-load')"
            @error="$emit('image-error')"
        ></b-image>
      </div>

      <div class="w-1/3">
        <SummaryTable
          :layers="defaultLayers"
          :company-alias="companyAlias"
          :get-total="getTotalElements"
          :get-executed="getExecutedElements"
          :get-incidents="getTotalIncidentElements"
          :get-progress="getProgressPictograms"
          :use-executed-ratio="companyAlias === 'ignis'"
        >
          <template #layer-action="{ layer }">
            <span v-if="imgPlant.type != layer.code" class="cursor-pointer text-blue-light"
              @click="$emit('show-image-plant', layer.code)">
              <b-icon icon="eye" pack="fas"></b-icon>
            </span>
          </template>
        </SummaryTable>

        <div v-if="pictograms.length" class="mt-3 mb-3">
          <h5 class="ext-blue-light font-light">{{ $t('pictograms') }}</h5>
        </div>

        <table class="w-full border border-blue-lighter border-collapse">
          <tbody>
            <tr v-for="item in pictograms"
              :key="item.id"
              @click="$emit('select-pictogram', item)"
              class="hover:bg-gray-100 cursor-pointer">
              <td class="p-2 border border-blue-lighter text-sm text-gray-800">
                {{ item.activity_planning_code + ' - ' + item.activity_associated }}</td>
              <td class="p-2 border border-blue-lighter text-sm text-gray-800 text-right whitespace-nowrap">
                {{ item.activity_progress }} <span class="ml-1">%</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<script>
import SummaryTable from "./SummaryTable.vue";

export default {
  name: "ProgressOverview",

  components: { SummaryTable },

  props: {
    pictograms: { type: Array, required: true },
    companyAlias: { type: String, required: true },
    imgPlant: { type: Object, required: true },
    isLoadingImage: { type: Boolean, default: false },
    hasViewer: { type: Boolean, default: false }, // hay power plant → muestra el icono de abrir visor
  },

  computed: {
    defaultLayers() {
      return [{ code: "pilings" }, { code: "structures" }, { code: "modules" }];
    },

    projectProgress() {
      if (this.pictograms.length > 0) {
        let total = 0;
        this.pictograms.forEach((item) => {
          total += parseFloat(item.activity_progress);
        });
        return this._truncateToFixed(total / this.pictograms.length, 2);
      }
      return 0;
    },

    formattedUpdatedAt() {
      if (!this.imgPlant.updatedAt) return null
      const d = new Date(this.imgPlant.updatedAt)
      if (isNaN(d.getTime())) return null
      return d.toLocaleString()
    },
  },

  methods: {
    getTotalElements(lyr) {
      if (this.pictograms.length > 0) {
        let total = 0;
        this.pictograms.forEach((item) => {
          if (item.type === "mechanical_installation")
            total += parseFloat(item.metadata["total_" + lyr.code]);
        });
        return total;
      }
      return 0;
    },

    getExecutedElements(lyr) {
      if (this.pictograms.length <= 0) return 0;

      let total = 0;
      if (this.companyAlias === "x-elio") {
        this.pictograms.forEach((item) => {
          if (item.metadata.total_progress != null) {
            total += parseFloat(item.metadata.total_progress[lyr.code] ?? 0);
          }
        });
      } else {
        this.pictograms.forEach((item) => {
          const layer = item.layers.find((l) => l.code === lyr.code);
          total += parseFloat(layer.metadata?.progress_layer?.elements_active ?? 0);
        });
      }
      return total;
    },

    getTotalIncidentElements(lyr) {
      if (this.pictograms.length > 0) {
        let total = 0;
        this.pictograms.forEach((item) => {
          item.layers.forEach((layer) => {
            if (layer.code === lyr.code && layer.metadata && layer.metadata["incident_statuses"] != null) {
              for (const key in layer.metadata["incident_statuses"]) {
                if (layer.metadata["incident_statuses"].hasOwnProperty(key)) {
                  total += parseFloat(layer.metadata["incident_statuses"][key].advance ?? 0);
                }
              }
            }
          });
        });
        return total;
      }
      return 0;
    },

    getProgressPictograms(lyr) {
      if (this.pictograms.length > 0) {
        let total = 0;
        this.pictograms.forEach((item) => {
          if (item.type === "mechanical_installation") {
            const layer = item.layers.find((l) => l.code === lyr.code);
            const metadata = layer.metadata;
            if (metadata && metadata["progress_layer"] != null) {
              total += parseFloat(metadata["progress_layer"]["advance_normalize"] ?? 0);
            }
          }
        });
        return total / this.pictograms.length;
      }
      return 0;
    },

    _truncateToFixed(num, decimals) {
      const factor = Math.pow(10, decimals);
      return Math.floor(num * factor) / factor;
    },
  },
};
</script>
